import React from 'react';
import Icon from '../../../components/AppIcon';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const AssessmentMetrics = ({ data }) => {
  const scoreDistributionData = data?.scoreDistribution?.map(item => ({
    name: item?.range,
    value: item?.count
  })) || [];

  const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#6b7280'];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const dataPoint = data?.scoreDistribution?.find(item => item?.range === payload?.[0]?.name);
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground">{payload?.[0]?.name}</p>
          <p className="text-sm text-muted-foreground">
            Count: <span className="font-semibold text-primary">{payload?.[0]?.value}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Percentage: <span className="font-semibold text-primary">{dataPoint?.percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Icon name="ClipboardCheck" size={20} className="text-purple-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Assessment Metrics</h3>
            <p className="text-sm text-muted-foreground">Performance analysis</p>
          </div>
        </div>
      </div>
      {/* Overall Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Overall Pass Rate</p>
          <p className="text-2xl font-bold text-foreground">{data?.overallPassRate}%</p>
        </div>
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Average Score</p>
          <p className="text-2xl font-bold text-foreground">{data?.averageScore}%</p>
        </div>
      </div>
      {/* Score Distribution Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-foreground mb-3">Score Distribution</h4>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={scoreDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} (${(percent * 100)?.toFixed(0)}%)`}
                outerRadius={60}
                fill="#8884d8"
                dataKey="value"
              >
                {scoreDistributionData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Retake Analysis */}
      <div className="pt-4 border-t border-border">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="RefreshCw" size={16} className="text-primary" />
          <h4 className="text-sm font-semibold text-foreground">Retake Analysis</h4>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">First Attempt Pass</span>
            <span className="font-semibold text-foreground">{data?.retakeAnalysis?.firstAttemptPass}%</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Second Attempt Pass</span>
            <span className="font-semibold text-foreground">{data?.retakeAnalysis?.secondAttemptPass}%</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Third+ Attempt Pass</span>
            <span className="font-semibold text-foreground">{data?.retakeAnalysis?.thirdAttemptPass}%</span>
          </div>
          <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
            <span className="text-muted-foreground">Average Retakes</span>
            <span className="font-semibold text-foreground">{data?.retakeAnalysis?.avgRetakes}</span>
          </div>
        </div>
      </div>
      {/* Top Performing Categories */}
      <div className="pt-4 border-t border-border mt-4">
        <h4 className="text-sm font-semibold text-foreground mb-3">Top Performing Categories</h4>
        <div className="space-y-2">
          {data?.attemptsByCategory?.slice(0, 3)?.map((cat, index) => (
            <div key={index} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground truncate flex-1">{cat?.name}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">{cat?.avgScore}%</span>
                <span className="text-xs text-muted-foreground">({cat?.passRate}% pass)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AssessmentMetrics;